import React from 'react';
import { Link } from 'react-router-dom';
import about from '../images/about_org.jpg';
import telecomDevices from '../images/telecom_devices.png';
import { useDocTitle } from '../components/CustomHook';
import CommonContainer from '../components/CommonContainer';
import { commonStyles } from '../pages/commonStyles';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LanIcon from '@mui/icons-material/Lan';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
/* ------------------------------------------------------------------ */
/* Small line icon used across the feature grid                       */
/* ------------------------------------------------------------------ */

const Icon = ({ d, className = '' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`w-7 h-7 ${className}`}
    aria-hidden="true"
  >
    {d}
  </svg>
);

/* ------------------------------------------------------------------ */
/* Page data                                                           */
/* ------------------------------------------------------------------ */

const VALUE_STRIP = [
  ['Keep your number', 'We handle the port, start to finish.', PhoneIcon],
  ['Zero-touch phones', "Plug in, and it's already your extension.", SmartphoneIcon],
  ['Carrier-grade trunks', 'Redundant SIP routes, not a hobby setup.', LanIcon],
  ['Local support', 'Birmingham metro. A person, not a ticket queue.', SupportAgentIcon],
];

const OLD_WAY = [
  'A change to the greeting takes days and costs money',
  "Calls to the office can't reach anyone working from home",
  'Voicemail lives on a box in the closet nobody can log into',
  'Nobody can tell you how many calls you missed last month',
  'Adding one employee means a truck roll and a new contract term',
];

const NEW_WAY = [
  'Greetings, hours and call routing change same-day — usually same-hour',
  'One extension rings the desk, the mobile app and the laptop together',
  'Voicemail arrives as an email with the recording attached',
  'Call reporting shows answered, missed and after-hours volume',
  'A new hire is a new extension — we ship a phone that self-configures',
];

const FEATURES = [
  {
    title: 'Auto attendant & IVR',
    body: '"Press 1 for sales." Multi-level menus, direct-dial by extension, and a dial-by-name directory — recorded in your own voice or ours.',
    d: (<><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M7 8h4M7 12h7M16 8h1M8 21h8" /></>),
  },
  {
    title: 'Ring groups & call flows',
    body: 'Ring the whole team at once, in order, or round-robin. Overflow to a cell, a group voicemail, or an after-hours message — on your schedule.',
    d: (<><circle cx="8" cy="8" r="3" /><circle cx="17" cy="7" r="2.5" /><path d="M2.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5M15.5 12.2c2 .5 3.4 2.1 3.9 4.3" /></>),
  },
  {
    title: 'Voicemail to email',
    body: 'Every message lands in the inbox as an audio attachment, so it can be forwarded, saved, or answered from the road without dialing in.',
    d: (<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>),
  },
  {
    title: 'Mobile & desktop apps',
    body: 'Your office extension on an iPhone, Android or laptop — outbound calls show the business number, so nobody hands out a personal cell again.',
    d: (<><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M11 18.5h2" /></>),
  },
  {
    title: 'Number porting',
    body: 'Keep every number you have — mains, direct lines, fax, the one on the truck. We run the port and schedule cutover outside business hours.',
    d: (<><path d="M12 3v10M8.5 6.5L12 3l3.5 3.5M4 14v4a3 3 0 003 3h10a3 3 0 003-3v-4" /></>),
  },
  {
    title: 'Zero-touch provisioning',
    body: 'Phones ship configured to your extensions, keys and directory. Staff plug in one cable; nothing is typed into a phone by hand.',
    d: (<><path d="M12 3l7.5 4v5.5c0 4.7-3.1 8-7.5 8.5-4.4-.5-7.5-3.8-7.5-8.5V7zM9.5 12l1.8 1.8L15 10" /></>),
  },
  {
    title: 'Hours & holiday routing',
    body: 'Open, closed, lunch, holidays, storm days. Calls follow the calendar automatically, and you can override it from your phone in seconds.',
    d: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>),
  },
  {
    title: 'Call reporting',
    body: 'Answered, missed, abandoned, average wait, busiest hour, per-extension totals. Enough to staff the phones with facts instead of guesses.',
    d: (<><path d="M4 20V10M10 20V4M16 20v-7M22 20v-4" /></>),
  },
  {
    title: 'E911 registration',
    body: 'Every extension is registered to a real dispatchable address, and updated when someone moves desks or works from a second location.',
    d: (<><path d="M12 2a7 7 0 017 7c0 5.2-7 13-7 13S5 14.2 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></>),
  },
];

const STEPS = [
  ['Discovery', 'We walk the office, list every number, extension, fax line and door phone, and check the network for call quality. You get a written plan and a fixed quote.'],
  ['Build', 'Your system is built and tested before anything ships — menus, hours, ring groups, voicemail, directory. You hear the greetings and approve the call flow first.'],
  ['Install & train', "Phones arrive pre-labeled by desk. We install on site, run the new system alongside the old one, and train staff on the ten things they'll actually use."],
  ['Port & cut over', 'Numbers port on a scheduled date, usually overnight or on a weekend. We stay on it through the first business day and adjust anything that feels wrong.'],
];

const PLANS = [
  {
    name: 'Essential',
    price: '$19.95',
    unit: '/ user / month',
    blurb: 'For small teams that mostly need the phone to ring in the right place.',
    items: [
      'Unlimited US & Canada calling',
      'Auto attendant and ring groups',
      'Voicemail to email',
      'Mobile and desktop apps',
      'One local number per user',
      'E911 registration',
    ],
    cta: 'Get a quote',
    featured: false,
  },
  {
    name: 'Business',
    price: '$24.95',
    unit: '/ user / month',
    blurb: 'The standard build for an office with a front desk, a schedule and a fax line.',
    items: [
      'Everything in Essential',
      'Multi-level IVR and hours/holiday routing',
      'Call reporting and missed-call tracking',
      'Call recording on request',
      'Conference bridge',
      'Analog adapter for fax or paging',
    ],
    cta: 'Get a quote',
    featured: true,
  },
  {
    name: 'Multi-site',
    price: 'Custom',
    unit: '',
    blurb: 'More than one location, or staff who need to reach each other by extension across town.',
    items: [
      'Everything in Business',
      'Shared dial plan across sites',
      'Site-to-site VPN for remote extensions',
      'Overhead paging and door intercom integration',
      'Dedicated SIP trunk per location',
      'On-site installation and training',
    ],
    cta: 'Talk it through',
    featured: false,
  },
];


const FAQS = [
  ['Can we keep our existing phone numbers?',
    <p>
      Yes — that's the normal case. We submit the port to your current carrier, and your numbers move to QComm without you losing calls. Ports usually take one to two weeks depending on the carrier, and we schedule the actual cutover outside your business hours. Your old service stays live until the port completes.
    </p>
  ],
  ['What internet connection do we need?',
    <p>
      Less than most people expect — roughly 100 kbps per simultaneous call, so ten people on the phone at once uses about 1 Mbps. What matters more than speed is stability. During discovery we test your connection and, if needed, recommend prioritizing voice traffic on your router so a large file upload doesn't chop up a customer call.
    </p>
  ],
  ['What happens if the internet or the power goes out?',
    <p>
      Calls keep arriving at the platform, which is hosted off‑site — so we fail them over automatically to mobile apps, cell phones or a voicemail message you choose in advance. It's configured before you need it, not during the outage.
    </p>
  ],
  ['Do we have to buy all new phones?',
    <p>
      Often not. QComm provisions over 30 phone manufacturers, so an existing fleet frequently comes across as‑is. Two things stop a phone from being reused: it's locked to your previous provider, or the manufacturer no longer issues firmware for it. Send us the models and MAC addresses and we'll tell you which ones are worth keeping.
    </p>
  ],
  ['Can staff work from home or on the road?',
    <p>
      Yes, and it doesn't require a separate product. The same extension rings on a desk phone, a mobile app and a laptop. Home‑based staff can also take a physical desk phone home and plug it into their own internet — it registers back to your system and behaves exactly as it does at the office.
    </p>
  ],
  ['Are we locked into a long contract?',
    <p>
      Month‑to‑month after installation, with hardware purchased outright or financed over the first year. Ask us for the specifics for your headcount.
    </p>
  ],
  ["What does support look like after we're live?",
    <p>
      Moves, adds, changes and greeting updates are included — email or call and they're done, typically the same day. Anything requiring an on‑site visit is scheduled, and we tell you the cost before we come. There is no per‑change fee for routine work.
    </p>
  ],
  ['What phones and devices we support?',
    <>
      <p>
        Every device is zero‑touch provisioned by QComm: plug it into your network, and it automatically pulls its extension, lines, speed dials, directory, and voicemail settings from our servers. No manual configuration, no per‑phone setup fee.

      </p>
      <div>  <Link
                  to="/telecommunications/supportedphones"
                 className="inline-flex items-center justify-center px-2 py-1 bg-blue-900 mt-1 hover:bg-blue-500 text-white text-md font-bold rounded-xl shadow-lg transition-all duration-300 hover:scale-105">

                  See supported phones & devices  <ArrowForwardIcon fontSize="medium" />
                </Link></div>
    </>
  ],
];



/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

function Telecom() {
  useDocTitle('Telecommunications');

  return (
   <div>
      {/* Hero Section */}
      <div className="relative overflow-hidden mt-4 lg:mt-0 h-[320px] md:h-[380px] lg:h-[450px]">
        <div className="absolute inset-0 z-0">
          <img
            src={about}
            alt="Telecommunications Background"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>

        <div className="relative z-20 h-full flex items-center justify-center text-center text-white">
          <h2 className={`${commonStyles.pageTitle} text-white`}>
            Telecommunications
          </h2>
        </div>
      </div>
          {/* Intro */}
      <div className="bg-gradient-to-br from-[#153E75] to-blue-900 py-8 md:py-10">
        <CommonContainer>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-[48px] font-bold leading-tight text-[#FFFFFF]">
                Your business phone system, hosted and handled.
              </h1>
              <p className="text-lg leading-8 text-blue-100 mb-6">
                QComm is Quadprosoft's cloud phone service for small and mid-sized businesses —
                keep the number your customers already dial, put a real desk phone on every
                desk, and stop paying a carrier for features that should have been free.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <Link
                  to="/contact"
                  className="rounded-xl bg-white px-8 py-3 text-lg font-semibold text-blue-900 transition hover:bg-blue-300"
                >
                  Get a quote
                </Link>
                {/* <Link
                  to="/telecommunications/supportedphones"
                  className="rounded-xl bg-white px-8 py-3 text-lg font-semibold text-blue-900 transition hover:bg-blue-300"
                >
                  See supported phones & devices
                </Link> */}
              </div>
            </div>
 
           <div className="hidden lg:flex justify-center items-center">
  <img
    src={telecomDevices}
    alt="Business cloud phone system with desk phone, mobile phone and laptop"
   className="w-full max-w-md md:max-w-lg lg:max-w-xl h-auto object-contain"

  />
</div>


          </div>
        </CommonContainer>
      </div>

     {/* Value Strip */}
<div className="bg-[#153E75] text-white">
  <CommonContainer>
    <div className="py-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {VALUE_STRIP.map(([t, s, IconComponent]) => (
        <div key={t} className="text-sm leading-relaxed text-blue-100 text-center">
          <IconComponent style={{ fontSize: 40 }} className="text-white mb-3" />
          <strong className="block text-base text-white">{t}</strong>
          {s}
        </div>
      ))}
    </div>
  </CommonContainer>
</div>

          <CommonContainer>
            {/* Why Switch */}
            <div className="mt-10">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <p className="text-blue-500 text-sm font-bold tracking-[.14em] uppercase mb-3">
                  Why businesses move to QComm
                </p>
                <h1 className={`${commonStyles.heading} mb-4`}>
                  The phone bill is rarely the worst part
                </h1>
                <p className={commonStyles.paragraph}>
                  Most companies we meet aren't unhappy with the price. They're unhappy that
                  nothing about the phone system can be changed without a support ticket, a week,
                  and a fee.
                </p>
              </div>

          <div className="grid md:grid-cols-2 gap-7">
            <div className="rounded-xl border border-gray-200 bg-white p-8">
              <h3 className="text-lg font-bold uppercase tracking-wider text-blue-900">
                What you're living with
              </h3>
              <ul className="mt-5 divide-y divide-gray-200">
                {OLD_WAY.map((t) => (
                  <li key={t} className="flex gap-3 py-3 text-[15px] text-gray-600">
                    <span className="text-red-500 shrink-0 mt-0.5">✘</span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-blue-900 p-8 text-blue-50">
              <h3 className="text-lg font-bold uppercase tracking-wider text-white">
                What QComm does instead
              </h3>
              <ul className="mt-5 divide-y divide-white/15">
                {NEW_WAY.map((t) => (
                  <li key={t} className="flex gap-3 py-3 text-[15px]">
                    <span className="text-green-400 shrink-0 mt-0.5">✔</span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-16">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-blue-500 text-sm font-bold tracking-[.14em] uppercase mb-3">
              What's included
            </p>
            <h1 className={`${commonStyles.heading} mb-4`}>
              Everything in the platform, not in the upsell
            </h1>
            <p className={commonStyles.paragraph}>
              These aren't add-on modules. They're part of every QComm seat, on every plan.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-xl border border-gray-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-900">
                  <Icon d={f.d} />
                </div>
                <h3 className={commonStyles.cardTitle}>{f.title}</h3>
                <p className={`${commonStyles.cardText} mt-2`}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-16">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-blue-500 text-sm font-bold tracking-[.14em] uppercase mb-3">
              Onboarding
            </p>
            <h1 className={`${commonStyles.heading} mb-4`}>
              How a cutover actually goes
            </h1>
            <p className={commonStyles.paragraph}>
              Most small offices are live in two to three weeks, and the only day anyone
              notices is the day the old system stops ringing.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map(([title, body], i) => (
              <div
                key={title}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-900 text-lg font-bold text-white mb-4">
                  {i + 1}
                </div>
                <h3 className={commonStyles.cardTitle}>{title}</h3>
                <p className={`${commonStyles.cardText} mt-2`}>{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Handsets */}
        <div id="phones" className="mt-16 scroll-mt-24">
          <h1 className={`${commonStyles.heading} mb-6`}>
            Bring Your Phones, Or Let Us Send New Ones
          </h1>
          <p className={`${commonStyles.paragraph} mb-4`}>
            QComm provisions more than 30 manufacturers — Yealink, Grandstream, Fanvil, Poly,
            Snom, Cisco, Avaya and more — so an existing fleet often comes with you instead of
            going in a dumpster.
          </p>
          <p className={commonStyles.paragraph}>
            For new deployments we standardize on Yealink for desks, DECT for staff who move
            around, and analog adapters for the fax machine and the overhead paging horn that
            everyone forgets about until cutover day.
          </p>
        </div>

        {/* Plans */}
        <div className="mt-16">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-blue-500 text-sm font-bold tracking-[.14em] uppercase mb-3">
              Plans
            </p>
            <h1 className={`${commonStyles.heading} mb-4`}>
              Priced per user, quoted in a day
            </h1>
            <p className={commonStyles.paragraph}>
              No 36-month term to get a reasonable rate, and no charge to change a greeting,
              add a ring group or move an extension.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {PLANS.map((p) => (
              <div
                key={p.name}
                className="relative bg-white rounded-xl shadow-lg p-8 flex flex-col
                           border border-gray-200
                           hover:bg-slate-500 hover:shadow-2xl hover:border-slate-500
                           transition-all duration-300 group"
              >
                {p.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-blue-900 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white">
                    Most chosen
                  </span>
                )}
                <h3 className={`${commonStyles.cardTitle} group-hover:text-white`}>
                  {p.name}
                </h3>
                <div className="mt-2 text-3xl font-bold text-blue-900 group-hover:text-white">
                  {p.price}{' '}
                  <small className="text-sm font-semibold text-gray-500 group-hover:text-blue-100">
                    {p.unit}
                  </small>
                </div>
                <p className={`${commonStyles.cardText} mt-2 group-hover:!text-white`}>
                  {p.blurb}
                </p>
                <ul className="my-6 space-y-2 flex-1">
                  {p.items.map((i) => (
                    <li
                      key={i}
                      className="flex gap-2 text-[15px] text-gray-600 group-hover:!text-white"
                    >
                      <span className="text-green-500 group-hover:text-green-300">✔</span>
                      {i}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="primary-btn text-center group-hover:bg-white group-hover:!text-slate-700"
                >
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[15px] text-gray-500">
            Hardware sold outright or bundled — your choice. Taxes and regulatory fees billed
            at cost.
          </p>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <p className="text-blue-500 text-sm font-bold tracking-[.14em] uppercase mb-3">
              Questions
            </p>
            <h1 className={`${commonStyles.heading}`}>The things everyone asks first</h1>
          </div>
          <div className="max-w-4xl mx-auto">
            {FAQS.map(([q, a], i) => (
              <details
                key={q}
                open={i === 0}
                className="group border-b border-gray-200 py-5"
              >
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-lg font-bold text-blue-900">
                  {q}
                  <span className="text-2xl font-normal text-blue-500 group-open:hidden">+</span>
                  <span className="hidden text-2xl font-normal text-blue-500 group-open:inline">–</span>
                </summary>
                <p className="mt-3 text-[15px] leading-8 text-gray-600">{a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-16 rounded-xl bg-gradient-to-br from-[#153E75] to-blue-900 py-16 px-6 text-center text-white">
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            Find out what your phone system should cost
          </h1>
          <p className="mt-4 text-lg leading-8 text-blue-100 max-w-2xl mx-auto">
            Send us a recent phone bill and a rough headcount. We'll come back within one
            business day with a written quote, a build plan, and an honest answer about
            whether switching is worth it for you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="rounded-xl bg-white px-8 py-3 text-lg font-semibold text-blue-900 transition hover:bg-blue-300"
            >
              Request a quote
            </Link>
            {/* TODO: real sales number
            <a
              href="tel:+1205XXXXXXX"
              className="rounded-xl border-2 border-white/60 px-8 py-3 text-lg font-semibold text-white transition hover:border-white hover:bg-white/10"
            >
              Call (205) ___-____
            </a> */}
          </div>
        </div>

        {/* Get Started */}
        <div className="my-16">
         
          <p className={`${commonStyles.paragraph} mt-8 text-center`}>
            To get your written quote, please{' '}
            <Link to="/contact" className="text-blue-500 hover:underline">
              contact us
            </Link>
            .
          </p>
        </div>
      </CommonContainer>
    </div>
  );
}

export default Telecom;
